// chunkUtils.ts - Chunk splitting and upload utilities for chunked uploaders

/**
 * Default chunk size constants
 */
export const CHUNK_SIZES = {
  SMALL: 2 * 1024 * 1024,   // 2MB - for training data
  MEDIUM: 5 * 1024 * 1024,  // 5MB - for most uploads (optimal balance)
  LARGE: 8 * 1024 * 1024,   // 8MB - for large files
} as const;

const encoder = new TextEncoder();
const decoder = new TextDecoder();

const NEWLINE_BYTE = 0x0a;

/** True for a UTF-8 continuation byte (0b10xxxxxx), i.e. not a sequence start. */
function isContinuationByte(byte: number): boolean {
  return (byte & 0xc0) === 0x80;
}

/**
 * Generate a unique upload ID
 * Uses crypto.randomUUID if available, falls back to secure alternatives
 */
export function generateUploadId(prefix: string = 'upload'): string {
  // Use crypto.randomUUID if available (secure)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID()}`;
  }

  // Fallback to crypto.getRandomValues (also secure)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    const hex = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    return `${prefix}_${hex}`;
  }

  // Last resort fallback (not recommended for production)
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Split string content into chunks, preserving line integrity.
 *
 * `chunkSize` is a HARD cap on the UTF-8 byte length of every returned chunk,
 * because the backend rejects any chunk over MAX_CHUNK_BYTES with 413
 * "chunk exceeds maximum size" (domains/upload/api/upload_limits.py).
 *
 * Two earlier assumptions broke that cap:
 *   1. the split walked UTF-16 code units, so multi-byte characters made the
 *      encoded chunk larger than the requested size, and
 *   2. the chunk was extended FORWARD to the next newline, which pushed it
 *      past the budget by up to one line. With CHUNK_SIZES.LARGE (8 MiB,
 *      equal to the server cap) that overshoot alone made every CSV over
 *      8 MiB fail on the first chunk.
 *
 * So the boundary is now searched backwards: end on the last line ending that
 * still fits. Chunks are reassembled by simple concatenation server-side, so
 * an over-long line is cut mid-line rather than allowed to blow the budget —
 * never mid UTF-8 sequence, since each chunk is decoded on its own.
 *
 * @param content - The string content to split
 * @param chunkSize - Maximum chunk size in bytes
 */
export function splitContentIntoChunks(
  content: string,
  chunkSize: number = CHUNK_SIZES.MEDIUM
): string[] {
  const bytes = encoder.encode(content);
  const chunks: string[] = [];
  let currentPosition = 0;

  while (currentPosition < bytes.length) {
    let endPosition = Math.min(currentPosition + chunkSize, bytes.length);

    if (endPosition < bytes.length) {
      const lastNewline = bytes.lastIndexOf(NEWLINE_BYTE, endPosition - 1);

      if (lastNewline >= currentPosition) {
        endPosition = lastNewline + 1; // Include \n
      } else {
        // Single line longer than the budget: back off to a sequence start.
        while (
          endPosition > currentPosition + 1 &&
          isContinuationByte(bytes[endPosition])
        ) {
          endPosition--;
        }
      }
    }

    chunks.push(decoder.decode(bytes.subarray(currentPosition, endPosition)));
    currentPosition = endPosition;
  }

  return chunks;
}



/**
 * Convert string chunk to Uint8Array
 */
export function encodeChunk(content: string): Uint8Array {
  return encoder.encode(content);
}


